import { X } from 'lucide-react';
import { useEffect, useState } from 'react';

interface PopupData {
    id: number;
    titulo: string;
    descripcion: string | null;
    imagen: string | null;
    link_boton: string | null;
    texto_boton: string | null;
    mostrar_solo_una_vez: boolean;
    delay_segundos: number;
    posicion: 'center' | 'top' | 'bottom';
}

interface PopupModalProps {
    popups: PopupData[];
}

const PopupModal = ({ popups }: PopupModalProps) => {
    const [currentPopupIndex, setCurrentPopupIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
        console.log('🎉 PopupModal - Popups recibidos:', popups);

        if (!popups || popups.length === 0) {
            console.log('❌ No hay popups para mostrar');
            return;
        }

        const popup = popups[currentPopupIndex];

        // Mostrar popup después del delay (sin verificar sessionStorage)
        console.log(`⏱️ Popup se mostrará en ${popup.delay_segundos} segundos`);
        const timer = setTimeout(() => {
            console.log('✅ Mostrando popup:', popup.titulo);
            setIsVisible(true);
            setIsAnimating(true);
        }, popup.delay_segundos * 1000);

        return () => clearTimeout(timer);
    }, [popups, currentPopupIndex]);

    const handleClose = () => {
        setIsAnimating(false);
        setTimeout(() => {
            setIsVisible(false);

            // Mostrar siguiente popup si existe
            if (currentPopupIndex < popups.length - 1) {
                setCurrentPopupIndex(currentPopupIndex + 1);
            }
        }, 300);
    };

    const handleButtonClick = () => {
        handleClose();
    };

    if (!popups || popups.length === 0) {
        return <div aria-hidden="true" className="hidden" />;
    }

    const popup = popups[currentPopupIndex];

    const positionClasses = {
        center: 'items-center justify-center',
        top: 'items-start justify-center pt-20',
        bottom: 'items-end justify-center pb-20',
    };

    return (
        <div
            className={`fixed inset-0 z-[9999] flex ${positionClasses[popup.posicion]} transition-opacity duration-300 ${isVisible && isAnimating ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
                }`}
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
            onClick={isVisible ? handleClose : undefined}
            aria-hidden={!isVisible}
        >
            <div
                className={`relative bg-white rounded-2xl shadow-2xl max-w-sm sm:max-w-md md:max-w-lg w-full mx-4 overflow-y-auto max-h-[80vh] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none'] transform transition-all duration-300 ${isVisible && isAnimating ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
                    }`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Botón cerrar */}
                <button
                    onClick={handleClose}
                    className="absolute top-3 right-3 z-10 bg-white/90 hover:bg-white rounded-full p-1.5 shadow-lg transition-all hover:scale-110"
                    aria-label="Cerrar"
                    disabled={!isVisible}
                >
                    <X className="w-4 h-4 text-gray-700" />
                </button>

                {/* Imagen */}
                {popup.imagen && (
                    <div className="w-full shrink-0 overflow-hidden">
                        <img
                            src={popup.imagen}
                            alt={popup.titulo}
                            className="w-full h-auto max-h-[30vh] md:max-h-[40vh] object-cover"
                            loading="lazy"
                        />
                    </div>
                )}

                {/* Contenido */}
                <div className="p-4 sm:p-5 md:p-6">
                    <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-4">
                        {popup.titulo}
                    </h2>

                    {popup.descripcion && (
                        <p className="text-sm sm:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed">
                            {popup.descripcion}
                        </p>
                    )}

                    {/* Botón de acción */}
                    {popup.link_boton && popup.texto_boton && (
                        <a
                            href={popup.link_boton}
                            onClick={handleButtonClick}
                            className="block w-full bg-gradient-to-r from-[#790546] to-[#9a0a5a] text-white text-center py-3 sm:py-4 px-4 sm:px-6 rounded-xl font-bold text-base sm:text-lg hover:from-[#6a0440] hover:to-[#890850] transition-all shadow-lg hover:shadow-xl hover:scale-105"
                            tabIndex={isVisible ? 0 : -1}
                        >
                            {popup.texto_boton}
                        </a>
                    )}

                    {/* Botón cerrar alternativo si no hay link */}
                    {!popup.link_boton && (
                        <button
                            onClick={handleClose}
                            className="block w-full bg-gray-200 text-gray-700 text-center py-2.5 sm:py-3 px-4 sm:px-6 rounded-xl font-semibold text-sm sm:text-base hover:bg-gray-300 transition-all"
                            disabled={!isVisible}
                        >
                            Cerrar
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PopupModal;
